import { DataTable } from "@/components/data-table/data-table";
import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { DataTableToolbar } from "@/components/data-table/data-table-toolbar";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Field, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useDataTable } from "@/hooks/use-data-table";
import { Head, router, useForm, usePage } from "@inertiajs/react";
import type { ColumnDef } from "@tanstack/react-table";
import {
  Camera,
  EyeIcon,
  Loader2,
  MailPlusIcon,
  PencilIcon,
  TrashIcon,
} from "lucide-react";
import { type FormEvent, useMemo, useRef, useState } from "react";

type User = {
  id: number;
  name: string | null;
  email: string;
  avatar: string | null;
  role: string;
  orgRole: string | null;
  superadmin: boolean;
  createdAt: string;
};

const roleLabels: Record<string, string> = {
  admin: "Administrador",
  member: "Membro",
  superadmin: "Super Admin",
};

function InviteUserDialog() {
  const [open, setOpen] = useState(false);
  const { user: currentUser, organization } = usePage().props;
  const isSuperadmin = currentUser?.role === "superadmin";
  const noOrgMode = !organization;

  const { data, setData, post, processing, reset } = useForm({
    invitation: {
      email: "",
      role: noOrgMode ? "superadmin" : "member",
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    post("/app/invitations", {
      onSuccess: () => {
        reset();
        setOpen(false);
      },
    });
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="font-normal">
          <MailPlusIcon />
          Convidar
        </Button>
      </DialogTrigger>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Convidar Usuário</DialogTitle>
            <DialogDescription>
              {noOrgMode
                ? "Convide um novo super administrador para a plataforma."
                : "Envie um convite por e-mail para adicionar um novo membro."}
            </DialogDescription>
          </DialogHeader>
          <FieldGroup className="py-4">
            <Field>
              <FieldLabel htmlFor="invite-email">E-mail</FieldLabel>
              <Input
                id="invite-email"
                type="email"
                value={data.invitation.email}
                onChange={(e) =>
                  setData("invitation", {
                    ...data.invitation,
                    email: e.target.value,
                  })
                }
                placeholder="usuario@exemplo.com"
                required
              />
            </Field>
            <Field>
              <FieldLabel>Papel</FieldLabel>
              <Select
                value={data.invitation.role}
                onValueChange={(value) =>
                  setData("invitation", { ...data.invitation, role: value })
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Selecione um papel" />
                </SelectTrigger>
                <SelectContent>
                  {isSuperadmin && noOrgMode && (
                    <SelectItem value="superadmin">Super Admin</SelectItem>
                  )}
                  {!noOrgMode && (
                    <>
                      <SelectItem value="admin">Administrador</SelectItem>
                      <SelectItem value="member">Membro</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </Field>
          </FieldGroup>
          <DialogFooter>
            <Button type="submit" disabled={processing}>
              Enviar Convite
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DeleteUserButton({ id }: { id: number }) {
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={() => {
        if (confirm("Tem certeza que deseja excluir este usuário?")) {
          router.delete(`/app/users/${id}`);
        }
      }}
    >
      <TrashIcon className="size-4" />
    </Button>
  );
}

function EditUserDialog({ user }: { user: User }) {
  const [open, setOpen] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const { organization } = usePage().props;
  const noOrgMode = !organization;

  const { data, setData, patch, processing, errors, reset } = useForm({
    user: {
      name: user.name ?? "",
      email_address: user.email,
      avatar: null as File | null,
    },
    role: user.orgRole ?? "",
  });

  const avatarPreview = data.user.avatar
    ? URL.createObjectURL(data.user.avatar)
    : user.avatar;

  const initials = (user.name ?? user.email)
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    patch(`/app/users/${user.id}`, {
      forceFormData: true,
      onSuccess: () => {
        reset();
        setOpen(false);
      },
    });
  }

  function handleOpenChange(isOpen: boolean) {
    setOpen(isOpen);
    if (isOpen) {
      reset();
    }
  }

  function handleAvatarChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) {
      setData("user", { ...data.user, avatar: file });
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" title="Editar">
          <PencilIcon className="size-4" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Editar Usuário</DialogTitle>
            <DialogDescription>
              Atualize as informações do usuário.
            </DialogDescription>
          </DialogHeader>
          <FieldGroup className="py-4">
            <Field>
              <FieldLabel>Foto</FieldLabel>
              <div className="flex items-center gap-4">
                <div className="relative group">
                  <Avatar className="size-16">
                    <AvatarImage src={avatarPreview ?? undefined} />
                    <AvatarFallback className="text-lg">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <button
                    type="button"
                    onClick={() => avatarInputRef.current?.click()}
                    className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50 opacity-0 transition-opacity group-hover:opacity-100"
                  >
                    <Camera className="size-4 text-white" />
                  </button>
                  <input
                    ref={avatarInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleAvatarChange}
                  />
                </div>
              </div>
            </Field>
            <Field>
              <FieldLabel htmlFor={`edit-name-${user.id}`}>Nome</FieldLabel>
              <Input
                id={`edit-name-${user.id}`}
                value={data.user.name}
                onChange={(e) =>
                  setData("user", { ...data.user, name: e.target.value })
                }
                required
              />
              {errors["user.name"] && (
                <p className="text-destructive text-sm">
                  {errors["user.name"]}
                </p>
              )}
            </Field>
            <Field>
              <FieldLabel htmlFor={`edit-email-${user.id}`}>E-mail</FieldLabel>
              <Input
                id={`edit-email-${user.id}`}
                type="email"
                value={data.user.email_address}
                onChange={(e) =>
                  setData("user", {
                    ...data.user,
                    email_address: e.target.value,
                  })
                }
                required
              />
              {errors["user.email_address"] && (
                <p className="text-destructive text-sm">
                  {errors["user.email_address"]}
                </p>
              )}
            </Field>
            {!noOrgMode && (
              <Field>
                <FieldLabel>Papel</FieldLabel>
                <Select
                  value={data.role}
                  onValueChange={(value) => setData("role", value)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Selecione um papel" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Administrador</SelectItem>
                    <SelectItem value="member">Membro</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            )}

          </FieldGroup>
          <DialogFooter>
            <Button type="submit" disabled={processing}>
              {processing && (
                <Loader2 className="mr-2 size-4 animate-spin" />
              )}
              Salvar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Index({ users }: { users: User[] }) {
  const { user: currentUser, impersonation } = usePage().props;

  const isSuperadmin = currentUser?.role === "superadmin" && !impersonation?.active;
  const isAdmin = currentUser?.role === "admin" || currentUser?.role === "superadmin";

  const columns = useMemo<ColumnDef<User>[]>(
    () => [
      {
        id: "name",
        accessorFn: (row) => row.name ?? "—",
        enableColumnFilter: true,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Nome" />
        ),
        cell: ({ row }) => (
          <span className="font-medium">{row.original.name ?? "—"}</span>
        ),
        meta: {
          label: "Nome",
          variant: "text" as const,
          placeholder: "Filtrar por nome...",
        },
      },
      {
        id: "email",
        accessorFn: (row) => row.email,
        enableColumnFilter: true,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="E-mail" />
        ),
        cell: ({ row }) => row.original.email,
        meta: {
          label: "E-mail",
          variant: "text" as const,
          placeholder: "Filtrar por e-mail...",
        },
      },
      {
        id: "role",
        accessorFn: (row) => row.role,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Papel" />
        ),
        meta: {
          label: "Papel",
        },
        cell: ({ row }) => {
          const { role } = row.original;
          if (role) {
            return (
              <Badge
                variant={role === "superadmin" ? "default" : "secondary"}
              >
                {roleLabels[role] ?? role}
              </Badge>
            );
          }
          return <span className="text-muted-foreground">—</span>;
        },
      },
      {
        id: "createdAt",
        accessorKey: "createdAt",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Criado em" />
        ),
        cell: ({ row }) =>
          new Date(row.original.createdAt).toLocaleDateString("pt-BR"),
        meta: {
          label: "Criado em",
        },
      },
      {
        id: "actions",
        cell: ({ row }) => {
          const canImpersonate =
            isSuperadmin && row.original.role !== "superadmin";

          return (
            <div className="flex items-center gap-1">
              {isAdmin && <EditUserDialog user={row.original} />}
              {canImpersonate && (
                <Button
                  variant="ghost"
                  size="icon"
                  title="Personificar"
                  onClick={() =>
                    router.post("/app/impersonation", {
                      user_id: row.original.id,
                    })
                  }
                >
                  <EyeIcon className="size-4" />
                </Button>
              )}
              {isSuperadmin && <DeleteUserButton id={row.original.id} />}
            </div>
          );
        },
        size: 120,
      },
    ],
    [isSuperadmin, isAdmin],
  );

  const { table } = useDataTable({
    data: users,
    columns,
    pageCount: Math.ceil(users.length / 10),
    enableRowSelection: false,
  });

  return (
    <>
      <Head title="Usuários" />
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Usuários</h1>
        <p className="text-muted-foreground text-sm">
          Gerencie os usuários da plataforma.
        </p>
      </div>
      <DataTable table={table}>
        <DataTableToolbar table={table}>
          {isAdmin && <InviteUserDialog />}
        </DataTableToolbar>
      </DataTable>
      </div>
    </>
  );
}
